import { isProgressStepperItemActive, isProgressStepperItemChecked, setProgressStepperItemActiveDescendant, setProgressStepperItemChecked, setProgressStepperItemStatus } from '../progress-stepper-item/utils';
import { defineCustomElement, getAttribute, getReactEventHandler, getRect, getTargetByAttribute, NectaryElement, updateAttribute } from '../utils';
const templateHTML = '<style>:host{display:block}#wrapper{display:flex;width:100%}::slotted(sinch-progress-stepper-item){flex:1;min-width:0}</style><div id="wrapper"><slot></slot></div>';
const template = document.createElement('template');
template.innerHTML = templateHTML;
defineCustomElement('sinch-progress-stepper', class extends NectaryElement {
  #$slot;
  #controller = null;
  #$items = [];
  #currentActiveDescendantIndex = -1;
  #isGoingToFocusItem = false;
  constructor() {
    super();
    const shadowRoot = this.attachShadow();
    shadowRoot.appendChild(template.content.cloneNode(true));
    this.#$slot = shadowRoot.querySelector('slot');
  }
  connectedCallback() {
    this.#controller = new AbortController();
    const {
      signal
    } = this.#controller;
    const options = {
      signal
    };
    this.role = 'tablist';
    this.#$slot.addEventListener('click', this.#onOptionClick, options);
    this.#$slot.addEventListener('keydown', this.#onOptionKeydown, options);
    this.#$slot.addEventListener('focusout', this.#onOptionBlur, options);
    this.addEventListener('-change', this.#onChangeReactHandler, options);
    queueMicrotask(() => {
      this.#$slot.addEventListener('slotchange', this.#onSlotChange, options);
      this.#onSlotChange();
    });
  }
  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = null;
  }
  static get observedAttributes() {
    return ['value', 'progressvalue'];
  }
  attributeChangedCallback(name, _, newVal) {
    switch (name) {
      case 'value':
        {
          this.#onValueChange(newVal);
          break;
        }
      case 'progressvalue':
        {
          this.#updateProgressValue();
          break;
        }
    }
  }
  set value(value) {
    updateAttribute(this, 'value', value);
  }
  get value() {
    return getAttribute(this, 'value', '');
  }
  set progressValue(value) {
    updateAttribute(this, 'progressvalue', value);
  }
  get progressValue() {
    return getAttribute(this, 'progressvalue', '');
  }
  nthOptionRect(index) {
    const $el = this.#$slot.assignedElements()[index];
    if ($el != null) {
      return getRect($el);
    }
    return null;
  }
  #onSlotChange = () => {
    this.#$items = this.#$slot.assignedElements();
    this.#onValueChange(this.value);
    this.#updateProgressValue();
  };
  #onOptionClick = e => {
    const target = getTargetByAttribute(e, 'value');
    if (target !== null && isProgressStepperItemActive(target)) {
      this.dispatchEvent(new CustomEvent('-change', {
        detail: getAttribute(target, 'value')
      }));
    }
  };
  #onValueChange(value) {
    for (const $item of this.#$items) {
      const isChecked = value === getAttribute($item, 'value');
      setProgressStepperItemChecked($item, isChecked);
    }
    this.#resetActiveDescendant();
  }
  #focusNextItem() {
    for (let i = 0; i < this.#$items.length; i++) {
      const nextIndex = (this.#currentActiveDescendantIndex + 1 + i) % this.#$items.length;
      const item = this.#$items[nextIndex];
      if (isProgressStepperItemActive(item)) {
        setProgressStepperItemActiveDescendant(item, true);
        this.#isGoingToFocusItem = true;
        item.focus();
        if (this.#currentActiveDescendantIndex >= 0) {
          setProgressStepperItemActiveDescendant(this.#$items[this.#currentActiveDescendantIndex], false);
        }
        this.#currentActiveDescendantIndex = nextIndex;
        break;
      }
    }
  }
  #focusPrevItem() {
    for (let i = 0; i < this.#$items.length; i++) {
      const nextIndex = (this.#currentActiveDescendantIndex - i - 1 + this.#$items.length) % this.#$items.length;
      const item = this.#$items[nextIndex];
      if (isProgressStepperItemActive(item)) {
        setProgressStepperItemActiveDescendant(item, true);
        this.#isGoingToFocusItem = true;
        item.focus();
        if (this.#currentActiveDescendantIndex >= 0) {
          setProgressStepperItemActiveDescendant(this.#$items[this.#currentActiveDescendantIndex], false);
        }
        this.#currentActiveDescendantIndex = nextIndex;
        break;
      }
    }
  }
  #updateProgressValue() {
    const progressValue = this.progressValue;
    const $items = this.#$slot.assignedElements();
    const $progressValueIndex = $items.findIndex(it => getAttribute(it, 'value') === progressValue);
    for (let i = 0; i < $items.length; i++) {
      if ($progressValueIndex < 0 || $progressValueIndex < i) {
        setProgressStepperItemStatus($items[i], 'inactive');
      } else if ($progressValueIndex > i) {
        setProgressStepperItemStatus($items[i], 'complete');
      } else {
        setProgressStepperItemStatus($items[i], 'incomplete');
      }
    }
    this.#resetActiveDescendant();
  }
  #getCheckedItemIndex() {
    for (let i = 0; i < this.#$items.length; i++) {
      if (isProgressStepperItemChecked(this.#$items[i])) {
        return i;
      }
    }
    return -1;
  }
  #getFirstActiveItemIndex() {
    for (let i = 0; i < this.#$items.length; i++) {
      if (isProgressStepperItemActive(this.#$items[i])) {
        return i;
      }
    }
    return -1;
  }
  #resetActiveDescendant() {
    if (this.#currentActiveDescendantIndex >= 0) {
      setProgressStepperItemActiveDescendant(this.#$items[this.#currentActiveDescendantIndex], false);
    }
    this.#currentActiveDescendantIndex = this.#getCheckedItemIndex();
    if (this.#currentActiveDescendantIndex >= 0) {
      setProgressStepperItemActiveDescendant(this.#$items[this.#currentActiveDescendantIndex], true);
      return;
    }
    this.#currentActiveDescendantIndex = this.#getFirstActiveItemIndex();
    if (this.#currentActiveDescendantIndex >= 0) {
      setProgressStepperItemActiveDescendant(this.#$items[this.#currentActiveDescendantIndex], true);
    }
  }
  #onOptionKeydown = e => {
    switch (e.code) {
      case 'Enter':
      case 'Space':
        {
          e.preventDefault();
          getTargetByAttribute(e, 'value')?.click();
          break;
        }
      case 'ArrowRight':
        {
          e.preventDefault();
          this.#focusNextItem();
          break;
        }
      case 'ArrowLeft':
        {
          e.preventDefault();
          this.#focusPrevItem();
          break;
        }
    }
  };
  #onOptionBlur = () => {
    if (this.#isGoingToFocusItem) {
      this.#isGoingToFocusItem = false;
      return;
    }
    this.#resetActiveDescendant();
  };
  #onChangeReactHandler = e => {
    getReactEventHandler(this, 'on-change')?.(e);
  };
});